import {
  resolveExerciseModality,
  parseIntervalCount,
  computeExerciseTarget,
  formatExercisePrescription,
  parsePaceKmToSec,
  parsePace100mToSec,
} from "@/lib/trainingDisciplines";
import { computeTargetWeight, formatIntensityLine } from "@/lib/strength";

function parseSetsReps(repsStr, setsStr) {
  const sets = parseInt(setsStr, 10) || 3;
  const repsMatch = String(repsStr || "10").match(/\d+/);
  const defaultReps = repsMatch ? parseInt(repsMatch[0], 10) : 10;
  return { sets, defaultReps };
}

function targetFieldsForSet(exercise, planDiscipline, ctx) {
  const target = computeExerciseTarget(exercise, planDiscipline, ctx);
  const mod = resolveExerciseModality(exercise, planDiscipline);

  if (mod === "strength") {
    const w = computeTargetWeight(exercise, ctx.liftMaxes || []);
    const pct = exercise.intensity_pct ? parseFloat(exercise.intensity_pct) : null;
    return { target_weight_kg: w, target_pct: pct };
  }
  if (mod === "running" && target?.type === "pace") {
    return { target_pace_sec_per_km: target.value };
  }
  if (mod === "cycling" && target?.type === "power") {
    return { target_power_w: target.value };
  }
  if (mod === "swimming" && target?.type === "pace_100m") {
    return { target_pace_sec_per_km: target.value };
  }
  return {};
}

export function initExerciseLogs(exercise, order, planDiscipline, lastExercise = null, ctx = {}) {
  const mod = resolveExerciseModality(exercise, planDiscipline);
  const prescription = formatExercisePrescription(exercise, planDiscipline, ctx);
  const count = parseIntervalCount(exercise, planDiscipline);
  const lastSets = lastExercise?.sets || [];
  const lastByNumber = Object.fromEntries(lastSets.map((s) => [s.set_number, s]));
  const targets = targetFieldsForSet(exercise, planDiscipline, ctx);

  const sets = [];
  for (let i = 1; i <= count; i++) {
    const prev = lastByNumber[i];
    const base = {
      set_number: i,
      completed: false,
      rpe: "",
      ...targets,
    };

    if (mod === "strength") {
      const { defaultReps } = parseSetsReps(exercise.reps, exercise.sets);
      const prefilledWeight = prev?.weight_kg ?? targets.target_weight_kg ?? "";
      sets.push({
        ...base,
        weight_kg: prefilledWeight !== null && prefilledWeight !== "" ? String(prefilledWeight) : "",
        reps: prev?.reps ?? defaultReps,
        last_weight_kg: prev?.weight_kg ?? null,
        last_reps: prev?.reps ?? null,
      });
    } else if (mod === "running") {
      const distM = exercise.distance_km ? Math.round(parseFloat(exercise.distance_km) * 1000) : "";
      sets.push({
        ...base,
        distance_m: prev?.distance_m ?? distM ?? "",
        duration_sec: prev?.duration_sec ?? "",
        pace_sec_per_km: prev?.pace_sec_per_km ?? targets.target_pace_sec_per_km ?? "",
        last_distance_m: prev?.distance_m ?? null,
        last_pace_sec_per_km: prev?.pace_sec_per_km ?? null,
      });
    } else if (mod === "cycling") {
      sets.push({
        ...base,
        duration_sec: prev?.duration_sec ?? (exercise.duration_min ? parseInt(exercise.duration_min, 10) * 60 : ""),
        power_w: prev?.power_w ?? targets.target_power_w ?? "",
        distance_m: prev?.distance_m ?? (exercise.distance_km ? Math.round(parseFloat(exercise.distance_km) * 1000) : ""),
        last_power_w: prev?.power_w ?? null,
      });
    } else if (mod === "swimming") {
      sets.push({
        ...base,
        distance_m: prev?.distance_m ?? exercise.distance_m ?? "",
        duration_sec: prev?.duration_sec ?? "",
        pace_sec_per_km: prev?.pace_sec_per_km ?? "",
        last_duration_sec: prev?.duration_sec ?? null,
      });
    } else {
      sets.push({
        ...base,
        duration_sec: prev?.duration_sec ?? (exercise.duration_min ? parseInt(exercise.duration_min, 10) * 60 : ""),
        notes: prev?.notes ?? "",
      });
    }
  }

  return {
    name: exercise.name,
    order,
    modality: mod,
    prescription,
    intensity_line: mod === "strength" ? formatIntensityLine(exercise, ctx.liftMaxes || []) : prescription,
    sets,
  };
}

export function calcSessionStats(exercises, planDiscipline) {
  let totalVolume = 0;
  let totalDistance = 0;
  let completedSets = 0;

  exercises.forEach((ex) => {
    ex.sets.forEach((s) => {
      if (!s.completed) return;
      completedSets++;
      const mod = ex.modality || planDiscipline;
      if (mod === "strength") {
        const w = parseFloat(s.weight_kg);
        const r = parseInt(s.reps, 10);
        if (w && r) totalVolume += w * r;
      }
      const dist = parseInt(s.distance_m, 10);
      if (dist) totalDistance += dist;
    });
  });

  return { totalVolume, totalDistance, completedSets };
}

export function buildSavePayload({
  selectedPlan,
  selectedDay,
  sessionDate,
  notes,
  duration,
  exercises,
  planDiscipline,
  externalProvider = null,
  externalActivityId = null,
}) {
  return {
    plan_id: selectedPlan || null,
    day_name: selectedDay,
    session_date: sessionDate,
    discipline: planDiscipline,
    notes: notes.trim() || null,
    duration_min: duration ? parseInt(duration, 10) : null,
    external_provider: externalProvider || null,
    external_activity_id: externalActivityId || null,
    exercises: exercises.map((ex) => ({
      name: ex.name,
      order: ex.order,
      modality: ex.modality,
      sets: ex.sets.map((s) => ({
        set_number: s.set_number,
        completed: s.completed,
        rpe: s.rpe !== "" && s.rpe != null ? parseFloat(s.rpe) : null,
        weight_kg: s.weight_kg !== "" ? parseFloat(s.weight_kg) : null,
        reps: s.reps !== "" ? parseInt(s.reps, 10) : null,
        distance_m: s.distance_m !== "" ? parseInt(s.distance_m, 10) : null,
        duration_sec: s.duration_sec !== "" ? parseInt(s.duration_sec, 10) : null,
        pace_sec_per_km: s.pace_sec_per_km !== "" ? parseFloat(s.pace_sec_per_km) : null,
        power_w: s.power_w !== "" ? parseFloat(s.power_w) : null,
        target_weight_kg: s.target_weight_kg ?? null,
        target_pct: s.target_pct ?? null,
        target_pace_sec_per_km: s.target_pace_sec_per_km ?? null,
        target_power_w: s.target_power_w ?? null,
      })),
    })),
  };
}

export function paceInputToSec(value, modality) {
  if (!value) return null;
  if (modality === "swimming") return parsePace100mToSec(value);
  return parsePaceKmToSec(value);
}